(function () {
  'use strict';
  if (window.MWAudioComparisonPlayer) return;
  const instances = new WeakMap();
  const playback = window.MWAudioPlayback;

  class AudioComparisonPlayer {
    constructor(options = {}) {
      const explicitTracks = Object.prototype.hasOwnProperty.call(options, 'tracks');
      if (explicitTracks && !Array.isArray(options.tracks)) throw new TypeError('Audio tracks must be an array.');
      const suppliedRoot = options.root;
      this.mountRoot = suppliedRoot;
      this.root = suppliedRoot
        ? (suppliedRoot.matches('.audio-compare-widget') ? suppliedRoot : suppliedRoot.querySelector('.audio-compare-widget'))
        : null;
      this.tracks = [];
      this.ready = Promise.resolve(this);
      if (!this.root || !explicitTracks) return;
      if (instances.has(this.root)) return instances.get(this.root);
      this.tracks = options.tracks.slice();
      this.document = this.root.ownerDocument;
      this.video = this.root.querySelector('#comparison-video');
      this.playButton = this.root.querySelector('.play-button');
      this.switchButtons = this.root.querySelectorAll('.switch-button');
      this.videoContainer = this.root.querySelector('.video-container');
      this.listeners = [];
      this.destroyed = false;
      this.playToken = null;
      this.videoPlayToken = null;
      this.loadSequence = 0;
      this.cancelVideoLoad = null;

      this.currentTrackIndex = 0;
      this.loadedTrackIndex = null;

      this.loadingOverlay = this.root.querySelector('.loading-overlay');
      this.progressBar = this.root.querySelector('.loading-progress-bar');
      this.loadingOverlay.style.display = 'flex';

      this.audioCache = new Map();
      this.videoCache = new Map();

      this.startTime = 0;
      this.pausedTime = 0;
      this.isPlaying = false;

      this.videoContainer.classList.add('bypassed');
      this.video.muted = true;

      instances.set(this.root, this);
      this.setupLifecycle();
      this.populateTrackList();
      if (!this.tracks.length) {
        this.hideLoading();
        return;
      }

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const contextOptions = this.isMobile() ? { latencyHint: 'playback', sampleRate: 44100 } : {};
      this.audioContext = new AudioContext(contextOptions);
      this.currentTrack = null;

      this.ready = this.initializePlayer();
    }

    async initializePlayer() {
      try {
        if (!await this.loadTrack(0, false)) return this;
        this.setupEventListeners();
        this.setupTrackButtons();
        this.setupMobileNavigation();
        this.hideLoading();
        requestAnimationFrame(() => this.updatePill());
        return this;
      } catch (error) {
        if (this.destroyed) return this;
        this.showError('Failed to initialize player');
        console.error(error);
      }
    }

    listen(target, event, listener) {
      target.addEventListener(event, listener);
      this.listeners.push(() => target.removeEventListener(event, listener));
    }

    setupLifecycle() {
      this.listen(this.document, 'shopify:section:unload', event => {
        if (event.target?.contains(this.root)) this.unmount();
      });
      this.listen(window, 'pagehide', () => {
        if (this.playToken !== null || this.isPlaying) this.stop();
      });
      if (window.MutationObserver && this.document.documentElement) {
        this.observer = new window.MutationObserver(() => {
          if (!this.root.isConnected) this.unmount();
        });
        this.observer.observe(this.document.documentElement, { childList: true, subtree: true });
      }
    }

    claimPlayback() {
      if (this.destroyed || this.root.isConnected === false) return null;
      this.playToken = playback.claim(this, () => this.stop());
      try {
        this.resumePromise = Promise.resolve(this.audioContext?.state === 'suspended' ? this.audioContext.resume() : undefined);
      } catch (error) { this.resumePromise = Promise.reject(error); }
      this.resumePromise.catch(() => {});
      return this.playToken;
    }

    ownsPlayback(token) {
      return !this.destroyed && this.root.isConnected !== false && token !== null && this.playToken === token && playback.isCurrent(this, token);
    }

    setupMobileNavigation() {
      const prevButton = this.root.querySelector('.prev-button');
      const nextButton = this.root.querySelector('.next-button');

      const handleNavClick = async (direction) => {
        if (this.destroyed || this.loadingOverlay.style.display === 'flex') return;
        const token = this.claimPlayback();

        try {
          const newIndex = direction === 'next'
            ? (this.currentTrackIndex + 1) % this.tracks.length
            : (this.currentTrackIndex - 1 + this.tracks.length) % this.tracks.length;

          if (!await this.switchTrack(newIndex, token)) return;
          await new Promise(resolve => setTimeout(resolve, 500));
          await this.play(token);
        } catch (error) {
          if (!this.ownsPlayback(token)) return;
          console.error('Navigation error:', error);
          this.showError('Failed to change track');
        }
      };

      this.listen(prevButton, 'click', () => handleNavClick('prev'));
      this.listen(nextButton, 'click', () => handleNavClick('next'));
    }

    async loadTrack(index, fromButton = false, token = null) {
      const sequence = ++this.loadSequence;
      this.cancelVideoLoad?.();
      const current = () => !this.destroyed && this.root.isConnected !== false && sequence === this.loadSequence && (token === null || this.ownsPlayback(token));
      try {
        if (!current()) return false;
        const track = this.tracks[index];
        this.currentTrackIndex = index;

        this.root.querySelector('.song-title h3').textContent = track.songTitle;

        const activeBtn = this.root.querySelector('.track button.active');
        if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        this.haltPlayback();

        const loaded = await new Promise((resolve, reject) => {
          const cleanup = () => {
            this.video.removeEventListener('loadeddata', onLoad);
            this.video.removeEventListener('error', onError);
            if (this.cancelVideoLoad === cancel) this.cancelVideoLoad = null;
          };
          const onLoad = () => {
            cleanup();
            resolve(true);
          };
          const onError = (error) => {
            cleanup();
            reject(error);
          };
          const cancel = () => { cleanup(); resolve(false); };
          this.cancelVideoLoad = cancel;
          this.video.addEventListener('loadeddata', onLoad);
          this.video.addEventListener('error', onError);
          this.video.src = track.video;
          this.video.load();
        });
        if (!loaded || !current()) return false;

        const [processedBuffer, bypassBuffer] = await Promise.all([this.fetchAudio(track.on), this.fetchAudio(track.off)]);
        if (!current()) return false;

        this.processedBuffer = processedBuffer;
        this.bypassBuffer = bypassBuffer;
        this.loadedTrackIndex = index;

        const isProcessed = this.root.querySelector('.switch-button.active').dataset.state === 'processed';
        this.currentTrack = this.createTrack(isProcessed ? processedBuffer : bypassBuffer);

        this.video.currentTime = 0.001;
        this.video.pause();
        return true;

      } catch (error) {
        if (!current()) return false;
        console.error('loadTrack error:', error);
        this.showError(`Failed to load track: ${this.tracks[index]?.songTitle || 'Unknown track'}`);
        throw error;
      }
    }

    async fetchAudio(url) {
      if (this.audioCache.has(url)) return this.audioCache.get(url);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      this.audioCache.set(url, audioBuffer);
      return audioBuffer;
    }

    async switchTrack(index, token = this.claimPlayback()) {
      try {
        if (!this.ownsPlayback(token)) return false;
        this.haltPlayback();
        this.currentTrackIndex = index;
        this.loadedTrackIndex = null;
        this.processedBuffer = null;
        this.bypassBuffer = null;

        if (window.gc) window.gc();

        this.loadingOverlay.style.display = 'flex';

        await new Promise(resolve => setTimeout(resolve, 100));
        if (!this.ownsPlayback(token)) return false;

        try {
          if (this.currentTrack) { this.currentTrack.stop(); this.currentTrack.disconnect(); }
          if (this.processedTrack) { this.processedTrack.stop(); this.processedTrack.disconnect(); }
          if (this.bypassTrack) { this.bypassTrack.stop(); this.bypassTrack.disconnect(); }
        } catch (e) {
        }

        if (!await this.loadTrack(index, false, token)) return false;

        if (!this.processedBuffer || !this.bypassBuffer) {
          throw new Error('Audio buffers not loaded');
        }

        this.processedTrack = this.createTrack(this.processedBuffer);
        this.bypassTrack = this.createTrack(this.bypassBuffer);

        const isProcessed = this.root.querySelector('.switch-button.active').dataset.state === 'processed';
        this.currentTrack = isProcessed ? this.processedTrack : this.bypassTrack;

        await new Promise(resolve => setTimeout(resolve, 500));
        if (!this.ownsPlayback(token)) return false;

        this.loadingOverlay.style.display = 'none';
        return true;

      } catch (error) {
        if (!this.ownsPlayback(token)) return false;
        console.error('switchTrack error:', error);
        this.stop();
        this.showError('Failed to switch track. Please try again.');
        this.loadingOverlay.style.display = 'none';
        return false;
      }
    }

    createTrack(buffer) {
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(this.audioContext.destination);
      return source;
    }

    async resumeAudioContext() {
      if (this.resumePromise) await this.resumePromise;
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    }

    async play(token) {
      if (token !== undefined && !this.ownsPlayback(token)) return false;
      if (this.isPlaying && this.ownsPlayback(this.playToken)) return true;
      if (token === undefined) token = this.claimPlayback();
      if (!this.ownsPlayback(token)) return false;
      try {
        if (this.loadedTrackIndex !== this.currentTrackIndex && !await this.switchTrack(this.currentTrackIndex, token)) return false;
        await this.resumeAudioContext();
        if (!this.ownsPlayback(token)) return false;

        this.video.currentTime = 0.001;
        this.videoPlayToken = token;
        await this.video.play();
        if (!this.ownsPlayback(token)) {
          if (this.videoPlayToken === token) this.video.pause();
          return false;
        }

        if (this.currentTrack) {
          try {
            this.currentTrack.start(0);
            this.startTime = this.audioContext.currentTime;
          } catch (error) {
            this.currentTrack = this.createTrack(this.currentTrack.buffer);
            this.currentTrack.start(0);
            this.startTime = this.audioContext.currentTime;
          }
        }

        this.isPlaying = true;
        this.playButton.classList.add('playing');
        this.playButton.querySelector('.play-icon').style.display = 'none';
        this.playButton.querySelector('.pause-icon').style.display = 'block';
        return true;
      } catch (error) {
        if (!this.ownsPlayback(token)) return false;
        this.stop();
        this.showError('Playback failed. Try reloading the page.');
        console.error(error);
        return false;
      }
    }

    haltPlayback() {
      if (this.currentTrack) {
        try { this.currentTrack.stop(); } catch (_) {}
      }

      this.video.pause();
      this.video.currentTime = 0.001;

      this.isPlaying = false;
      this.startTime = 0;
      this.pausedTime = 0;
      this.playButton.classList.remove('playing');
      this.playButton.querySelector('.play-icon').style.display = 'block';
      this.playButton.querySelector('.pause-icon').style.display = 'none';
    }

    stop() {
      playback.release(this, this.playToken);
      this.playToken = null;
      this.loadSequence++;
      this.cancelVideoLoad?.();
      this.haltPlayback();
      this.hideLoading();
    }

    destroy() {
      if (this.destroyed) return;
      this.stop();
      this.destroyed = true;
      this.listeners.forEach(remove => remove());
      this.listeners = [];
      this.observer?.disconnect();
      this.audioCache.clear();
      if (this.audioContext && this.audioContext.state !== 'closed') this.audioContext.close().catch(() => {});
      instances.delete(this.root);
    }

    unmount() {
      window.MWStorefrontAudio?.unmount?.(this.mountRoot);
      this.destroy();
    }

    switchAudio(state) {
      if (!this.processedBuffer || !this.bypassBuffer) return;

      const currentTime = this.isPlaying
        ? (this.audioContext.currentTime - this.startTime)
        : this.pausedTime;

      if (this.currentTrack) {
        try { this.currentTrack.stop(); } catch (_) {}
        this.currentTrack.disconnect();
      }

      const buffer = state === 'processed' ? this.processedBuffer : this.bypassBuffer;
      this.currentTrack = this.createTrack(buffer);

      if (this.isPlaying && this.ownsPlayback(this.playToken)) {
        this.currentTrack.start(0, currentTime % this.currentTrack.buffer.duration);
      }
    }

    setupEventListeners() {
      this.listen(this.playButton, 'click', () => {
        if (this.destroyed || this.loadingOverlay.style.display === 'flex') return;
        if (this.playToken === null) {
          this.play();
        } else {
          this.stop();
        }
      });

      this.switchButtons.forEach(button => {
        this.listen(button, 'click', () => {
          const state = button.dataset.state;

          this.switchButtons.forEach(btn => btn.classList.remove('active'));
          button.classList.add('active');
          this.updatePill();

          if (state === 'processed') {
            this.videoContainer.classList.add('bypassed');
          } else {
            this.videoContainer.classList.remove('bypassed');
          }

          this.switchAudio(state);
        });
      });

      this.listen(this.video, 'waiting', () => {
        if (this.isPlaying) this.loadingOverlay.style.display = 'flex';
      });
      this.listen(this.video, 'playing', () => {
        this.loadingOverlay.style.display = 'none';
      });

      this.listen(this.document, 'visibilitychange', () => {
        if (this.document.hidden && this.playToken !== null) {
          this.stop();
        }
      });
    }

    setupTrackButtons() {
      const trackButtons = this.root.querySelectorAll('.track button');

      trackButtons.forEach((button, index) => {
        this.listen(button, 'click', async () => {
          if (this.destroyed || this.loadingOverlay.style.display === 'flex') return;
          const token = this.claimPlayback();

          try {
            trackButtons.forEach(b => b.classList.remove('active'));
            button.classList.add('active');

            if (!await this.switchTrack(index, token)) return;
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.play(token);

          } catch (error) {
            if (!this.ownsPlayback(token)) return;
            console.error('Track button error:', error);
            this.showError('Failed to play track');
            button.classList.remove('active');
            trackButtons[this.currentTrackIndex].classList.add('active');
          }
        });
      });
    }

    populateTrackList() {
      const trackList = this.root.querySelector('.track-list');
      const rows = this.tracks.map((track, index) => {
        const row = this.document.createElement('div');
        row.className = 'track';
        const button = this.document.createElement('button');
        button.type = 'button';
        button.dataset.track = String(index);
        button.textContent = track.songTitle;
        row.appendChild(button);
        return row;
      });
      trackList.replaceChildren(...rows);
    }

    hideLoading() {
      this.loadingOverlay.style.display = 'none';
    }

    showError(message) {
      const alert = this.document.createElement('div');
      alert.className = 'alert';
      alert.textContent = message;
      this.root.appendChild(alert);

      setTimeout(() => alert.classList.add('show'), 10);
      setTimeout(() => {
        alert.classList.remove('show');
        setTimeout(() => alert.remove(), 300);
      }, 3000);
    }

    updatePill() {
      const activeBtn = this.root.querySelector('.switch-button.active');
      if (!activeBtn) return;
      const container = activeBtn.closest('.compare-switch');
      if (!container) return;
      container.style.setProperty('--pill-left', activeBtn.offsetLeft + 'px');
      container.style.setProperty('--pill-width', activeBtn.offsetWidth + 'px');
    }

    isMobile() {
      return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }
  }

  window.MWAudioComparisonPlayer = AudioComparisonPlayer;
})();
